import { useEffect } from 'react';
import { useSelectedPlanContext } from '../contexts/SelectedPlanContext';

/**
 * Hook to ensure default selections are made for colocation resources
 * @param {Object} resourcesData - The resources data from API
 * @param {Array} restrictedIds - List of restricted resource IDs
 * @param {boolean} isResourcesLoaded - Whether resources are loaded
 * @param {boolean} isRestrictedIdsLoaded - Whether restricted IDs are loaded
 */
const useDefaultSelections = (resourcesData, restrictedIds, isResourcesLoaded, isRestrictedIdsLoaded) => {
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();

	useEffect(() => {
		if (!isResourcesLoaded || !resourcesData) {
			return;
		}

		const ensureDefaultSelections = () => {
			let hasUpdates = false;
			const updates = {};

			console.log('useDefaultSelections: Checking selections', {
				resourcesData,
				selectedPlan,
				restrictedIds,
				isRestrictedIdsLoaded
			});

			// Define the mapping from resource type to plan key
			const resourceMapping = {
				racks: 'rack',
				energies: 'energy',
				bandwidths: 'bandwidth',
				prefixes: 'prefix'
			};

			Object.keys(resourcesData).forEach((resourceType) => {
				const resources = resourcesData[resourceType];
				const planKey = resourceMapping[resourceType];

				if (!planKey || !Array.isArray(resources) || resources.length === 0) {
					return;
				}

				const currentSelection = selectedPlan[planKey];
				const needsSelection = !currentSelection?.id || currentSelection.id === "";

				if (needsSelection) {
					console.log(`useDefaultSelections: Finding default for ${planKey}`, {
						currentSelection,
						availableResources: resources.length,
						restrictedIds: restrictedIds || []
					});

					let defaultItem = null;

					// Strategy 1: Find in_plan item that's not restricted
					if (isRestrictedIdsLoaded && restrictedIds) {
						defaultItem = resources.find(item => 
							item.in_plan && !restrictedIds.includes(item.id)
						);
					} else {
						// If restricted IDs not loaded yet, just find in_plan
						defaultItem = resources.find(item => item.in_plan);
					}

					// Strategy 2: Find first unrestricted item
					if (!defaultItem && isRestrictedIdsLoaded && restrictedIds) {
						defaultItem = resources.find(item => !restrictedIds.includes(item.id));
					}

					// Strategy 3: Fallback to first item (even if restricted)
					if (!defaultItem && resources.length > 0) {
						defaultItem = resources[0];
					}

					if (defaultItem) {
						console.log(`useDefaultSelections: Setting default for ${planKey}`, defaultItem);
						updates[planKey] = {
							id: defaultItem.id,
							value: defaultItem.value,
							order: defaultItem.order || 0,
							in_plan: defaultItem.in_plan || false,
						};
						hasUpdates = true;
					} else {
						console.warn(`useDefaultSelections: No valid default found for ${planKey}`);
					}
				}
			});

			if (hasUpdates) {
				console.log('useDefaultSelections: Applying updates', updates);
				changeSelectedPlan(prev => ({
					...prev,
					service: "colocation",
					...updates
				}));
			}
		};

		// Run immediately
		ensureDefaultSelections();

		// Also run after a small delay to catch any race conditions
		const timeoutId = setTimeout(ensureDefaultSelections, 100);

		return () => clearTimeout(timeoutId);
	}, [resourcesData, restrictedIds, isResourcesLoaded, isRestrictedIdsLoaded, selectedPlan, changeSelectedPlan]);
};

export default useDefaultSelections;
